import {
  Divider,
  Card,
  Button,
  Menu,
  MenuItem,
  Popover,
} from '@blueprintjs/core';
import { sms } from '@axo/shared/data-access/types';
import {
  SMSIcon,
  CreatorTag,
  CreatedAtTag,
  TransactionalIcon,
  FollowupTag,
  FailIcon,
} from '../CommunicationItem';
import cardClasses from '../CommunicationItem/Card.module.css';
import styles from './SMSPanel.module.css';
import { BrokerLogo } from '../../BrokerLogo';
import { ReactNode } from 'react';
import { formatDatetime } from '../../../utils/formatDate';

interface ISMSCardView {
  SMS: sms.SMS;
  template?: sms.SMSTemplate;
  to: string;
  markAsResolved: () => void;
}

export function SMSCardView({
  SMS,
  template,
  to,
  markAsResolved,
}: ISMSCardView) {
  let status: ReactNode;
  switch (SMS.Status) {
    case sms.SMSStatus.SMSStatusNew:
      status = 'New';
      break;
    case sms.SMSStatus.SMSStatusInFlight:
      status = 'In flight';
      break;
    case sms.SMSStatus.SMSStatusSent:
      status = 'Sent';

      if (SMS.SentAt) status += ` (${formatDatetime(SMS.SentAt)})`;

      break;
    case sms.SMSStatus.SMSStatusFailed:
      status = 'Failed';
      break;
    default:
      status = 'Unknown';
  }

  return (
    <Card className={cardClasses.card}>
      <div className={cardClasses.topBar}>
        <SMSIcon inbound={SMS.Inbound} />
        {SMS.Transactional && <TransactionalIcon />}
        <CreatorTag
          creatorID={SMS.CreatorID}
          customValueOverride={SMS.Inbound ? 'CUSTOMER' : undefined}
        />
        <CreatedAtTag
          label={SMS.Inbound ? 'Received at:' : 'Created at:'}
          createdAt={SMS.CreatedAt}
        />
        {SMS.UpdatedAt && (
          <CreatedAtTag label="Last updated at:" createdAt={SMS.UpdatedAt} />
        )}
        <div className={cardClasses.fill} />
      </div>
      {!SMS.Inbound && (
        <>
          <div className={cardClasses.formRow}>
            <span className={cardClasses.label}>Status:</span>
            <span className={cardClasses.fill}>{status}</span>
          </div>
          <div className={cardClasses.formRow}>
            <span className={cardClasses.label}>From:</span>
            <span className={cardClasses.fill}>
              <BrokerLogo
                className={styles.brokerLogo}
                broker={SMS.Meta.Broker}
                fallback="-"
              />
            </span>
          </div>
          <div className={cardClasses.formRow}>
            <span className={cardClasses.label}>To: </span>
            <span className={cardClasses.fill}>{to}</span>
          </div>
          {template && (
            <div className={cardClasses.formRow}>
              <span className={cardClasses.label}>Name:</span>
              <span>{template?.Name}</span>
            </div>
          )}
        </>
      )}
      <Divider />
      <div className={cardClasses.body}>
        <div>{SMS.Body}</div>
        <Divider />
        {SMS.Status === sms.SMSStatus?.SMSStatusFailed && (
          <div className={styles.failed}>
            <FailIcon />
            <div>
              {SMS.ErrorMessage ? SMS.ErrorMessage : 'Failed to deliver SMS'}
            </div>
          </div>
        )}
      </div>
      <div className={cardClasses.bottomRow}>
        <FollowupTag followupAt={SMS.FollowupAt} resolvedAt={SMS.ResolvedAt} />
        <div className={cardClasses.fill} />
        {SMS.FollowupAt && !SMS.ResolvedAt && (
          <Popover
            interactionKind="hover"
            content={
              <Menu>
                <MenuItem text="Mark as resolved" onClick={markAsResolved} />
              </Menu>
            }
          >
            <Button minimal icon="more" />
          </Popover>
        )}
      </div>
    </Card>
  );
}
